"use client"

import FolderSelector from "@/(admin)/modules/media/pages/FolderSelector"
import { addMedia, fetchMedia } from "@/(admin)/modules/media/services/apiServices"
import { resolveMediaDomainParam } from "@/(admin)/modules/media/utils/media-domain"
import { t } from "@/languages/i18n"
import { toImageSrc } from "@/lib/utils"
import { AlbumImage } from "@/stores/use-album-modal-store"
import { useDomainStore } from "@/stores/use-domain-store"
import { MediaData } from "@/types/types"
import { UploadOutlined } from "@ant-design/icons"
import { Checkbox, message, Spin, Upload, UploadFile } from "antd"
import { useCallback, useEffect, useState } from "react"

import { PAGE_SIZE } from "./constants"
import { mediaToAlbumImage } from "./utils"

interface ImagePickerProps {
  selected: AlbumImage[]
  onChange: (images: AlbumImage[]) => void
}

export default function ImagePicker({ selected, onChange }: ImagePickerProps) {
  const [folderId, setFolderId] = useState<number | null>(null) // null = "Tất cả tệp"
  const [data, setData] = useState<MediaData[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [refreshKey, setRefreshKey] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [fileList, setFileList] = useState<UploadFile[]>([])

  const selectedDomain = useDomainStore(s => s.selectedDomain)
  const domain = resolveMediaDomainParam(selectedDomain)

  const loadMedia = useCallback(() => {
    setLoading(true)
    fetchMedia(page, PAGE_SIZE, "", folderId, domain)
      .then(({ data, pagination }) => {
        setData(data)
        setTotal(pagination.total ?? 0)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [folderId, page, domain, refreshKey])

  useEffect(() => {
    loadMedia()
  }, [loadMedia])

  const handleCustomRequest = async (
    options: Parameters<Required<React.ComponentProps<typeof Upload>>["customRequest"]>[0],
  ) => {
    const { file, onSuccess, onError } = options
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append("files", file)
      const items = await addMedia(formData, folderId, domain)
      if (items.length > 0) {
        onSuccess?.(items)
        message.success(t("Media uploaded successfully!"))
        setRefreshKey(k => k + 1)
        setPage(1)
        const newImgs = items.map(mediaToAlbumImage)
        onChange([...selected, ...newImgs])
      } else {
        onError?.(new Error("Upload failed"))
        message.error(t("Failed to upload media."))
      }
    } catch (error) {
      console.error(error)
      onError?.(error instanceof Error ? error : new Error(String(error)))
      message.error(t("Failed to upload media."))
    } finally {
      setUploading(false)
    }
  }

  const handleUploadChange = (info: { fileList: UploadFile[] }) => {
    setFileList(info.fileList)
  }

  const toggleImage = useCallback(
    (m: MediaData) => {
      const img = mediaToAlbumImage(m)
      const exists = selected.some(s => s.url === img.url)
      if (exists) {
        onChange(selected.filter(s => s.url !== img.url))
      } else {
        onChange([...selected, img])
      }
    },
    [selected, onChange],
  )

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Toolbar */}
      <div style={styles.toolbar}>
        <FolderSelector
          compact
          currentFolderId={folderId}
          onFolderChange={id => {
            setFolderId(id)
            setPage(1)
            setRefreshKey(k => k + 1)
          }}
          refreshTrigger={refreshKey}
        />
        <div style={styles.toolbarRight}>
          {selected.length > 0 && (
            <span style={styles.selectedCount}>
              {selected.length} {t("selected")}
            </span>
          )}
          {selected.length > 0 && (
            <button onClick={() => onChange([])} style={styles.clearBtn}>
              {t("Clear selection")}
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        {/* Image grid */}
        <div style={{ flex: 1, overflowY: "auto", padding: 12, minWidth: 0 }}>
          {loading && data.length === 0 ? (
            <div style={styles.centerSpin}>
              <Spin />
            </div>
          ) : data.length === 0 && !uploading ? (
            <div style={styles.emptyState}>{t("No images found")}</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8 }}>
              {data.map(m => {
                const isSelected = selected.some(s => s.url === m.url)
                return <ImageGridItem key={m.id} media={m} isSelected={isSelected} onToggle={() => toggleImage(m)} />
              })}
              {uploading && (
                <div style={styles.uploadingSlot}>
                  <Spin size="small" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Dragger */}
        <div style={styles.draggerPanel}>
          <Upload.Dragger
            multiple
            fileList={fileList}
            customRequest={handleCustomRequest}
            onChange={handleUploadChange}
            disabled={uploading}
            showUploadList={false}
            style={{ height: "100%" }}
          >
            <div style={styles.draggerContent}>
              <UploadOutlined style={{ fontSize: 28, color: "#9ca3af" }} />
              <p style={{ fontSize: 11, color: "#666", textAlign: "center", margin: 0 }}>
                {t("Drag images here or click to upload")}
              </p>
            </div>
          </Upload.Dragger>
        </div>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <PaginationBar
          page={page}
          total={total}
          pageSize={PAGE_SIZE}
          data={data}
          selected={selected}
          onPageChange={setPage}
          onSelectPage={imgs => onChange([...selected, ...imgs])}
          onDeselectPage={urls => onChange(selected.filter(s => !urls.includes(s.url)))}
        />
      )}
    </div>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ImageGridItem({
  media,
  isSelected,
  onToggle,
}: {
  media: MediaData
  isSelected: boolean
  onToggle: () => void
}) {
  return (
    <div
      onClick={onToggle}
      style={{
        position: "relative",
        aspectRatio: "1",
        borderRadius: 6,
        overflow: "hidden",
        cursor: "pointer",
        border: `2px solid ${isSelected ? "#1677ff" : "transparent"}`,
        boxShadow: isSelected ? "0 0 0 2px rgba(22,119,255,0.2)" : "none",
        transition: "border-color 0.12s, box-shadow 0.12s",
      }}
    >
      <Checkbox checked={isSelected} style={{ position: "absolute", top: 4, left: 4, zIndex: 2 }} />
      <div
        style={{
          width: "100%",
          height: "100%",
          // CMS ưu tiên nét: dùng bản `large` (~1000px) nếu có, fallback thẳng về
          // ảnh gốc. Không tối ưu dung lượng vì đây là màn quản trị.
          backgroundImage: `url(${toImageSrc(media.formats?.large?.url || media.url)})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
    </div>
  )
}

function PaginationBar({
  page,
  total,
  pageSize,
  data,
  selected,
  onPageChange,
  onSelectPage,
  onDeselectPage,
}: {
  page: number
  total: number
  pageSize: number
  data: MediaData[]
  selected: AlbumImage[]
  onPageChange: (p: number) => void
  onSelectPage: (imgs: AlbumImage[]) => void
  onDeselectPage: (urls: string[]) => void
}) {
  const allSelected = data.length > 0 && data.every(m => selected.some(s => s.url === m.url))
  const someSelected = data.some(m => selected.some(s => s.url === m.url)) && !allSelected
  const totalPages = Math.ceil(total / pageSize)

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const toAdd = data.filter(m => !selected.some(s => s.url === m.url)).map(mediaToAlbumImage)
      onSelectPage(toAdd)
    } else {
      onDeselectPage(data.map(m => m.url))
    }
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        padding: "8px 12px",
        borderTop: "1px solid #eee",
        flexShrink: 0,
      }}
    >
      <Checkbox checked={allSelected} indeterminate={someSelected} onChange={e => handleSelectAll(e.target.checked)}>
        <span style={{ fontSize: 12 }}>{t("Select this page")}</span>
      </Checkbox>
      <div style={{ marginLeft: 16 }}>
        <button onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page === 1} style={styles.pageBtn}>
          ‹
        </button>
        <span style={{ fontSize: 12 }}>
          {page} / {totalPages}
        </span>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} style={styles.pageBtn}>
          ›
        </button>
      </div>
    </div>
  )
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  toolbar: {
    display: "flex",
    alignItems: "center" as const,
    gap: 8,
    padding: "8px 12px",
    borderBottom: "1px solid #eee",
    flexShrink: 0,
    flexWrap: "wrap" as const,
  },
  toolbarRight: {
    marginLeft: "auto",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  selectedCount: {
    fontSize: 12,
    color: "#1677ff",
    fontWeight: 500,
  },
  clearBtn: {
    background: "none",
    border: "none",
    color: "#ff4d4f",
    cursor: "pointer",
    fontSize: 12,
  },
  centerSpin: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 200,
  },
  emptyState: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 200,
    color: "#999",
    fontSize: 13,
  },
  uploadingSlot: {
    aspectRatio: "1",
    borderRadius: 6,
    border: "2px dashed #1677ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  draggerPanel: {
    borderLeft: "1px solid #eaeaef",
    flexShrink: 0,
    width: "16.67%",
    padding: 8,
  },
  draggerContent: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "16px 8px",
  },
  pageBtn: {
    marginRight: 8,
    padding: "4px 10px",
    borderRadius: 4,
    border: "1px solid #ddd",
    background: "#fff",
    cursor: "pointer",
    opacity: 0.5,
  },
}
